import { bound } from "@frui.ts/helpers";
import { computed } from "mobx";
import type { IRefund } from "entities/types";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import type RefundsSearchFilter from "entities/filters/refunds/refundsSearchFilter";
import type RefundType from "entities/refundType";
import type LicensePlate from "entities/licensePlate";
import type PayGateRefundResponse from "entities/payGateRefundResponse";
import type PayGatePaymentResponse from "entities/payGatePaymentResponse";
import type RefundTransferPayment from "entities/refundTransferPaymentResponse";
import type SenderBankAccount from "entities/refund/senderBankAccount";
import type TransferOriginalPayment from "entities/transferOriginalPaymentDetailResponse";
import type PayGateToTransferRefundDetail from "entities/payGateToTransferRefundDetailResponse";
import type RefundOriginalPayGatePaymentResponse from "entities/refundOriginalPayGatePaymentResponse";
import ApprovalListViewModel from "../../common/approvalListViewModel";
import RefundApprovalType from "entities/refundApprovalType";
import { Modules } from "config";
import { SelectableListViewModel } from "../../selectableListViewModel";

export default abstract class RefundApprovalsListViewModel<
  TEntity extends IRefund,
  TDetail extends ScreenBase,
  TFilter extends RefundsSearchFilter
> extends ApprovalListViewModel<RefundApprovalType, TEntity, TDetail, TFilter> {
  abstract readonly refundType: RefundType;
  readonly approvalType = RefundApprovalType;

  translateModelAttribute(model: "licensePlate", attribute: keyof LicensePlate): string;
  translateModelAttribute(model: "payGateRefund", attribute: keyof PayGateRefundResponse): string;
  translateModelAttribute(model: "payGateRefundPayment", attribute: keyof PayGatePaymentResponse): string;
  translateModelAttribute(model: "payGateToTransferRefund", attribute: keyof PayGateToTransferRefundDetail): string;
  translateModelAttribute(model: "senderBankAccount", attribute: keyof SenderBankAccount): string;
  translateModelAttribute(model: "refund", attribute: keyof IRefund): string;
  translateModelAttribute(model: "transferRefundPayment", attribute: keyof RefundTransferPayment): string;
  translateModelAttribute(model: "transferOriginalPayment", attribute: keyof TransferOriginalPayment): string;
  translateModelAttribute(
    model: "refundOriginalPayGatePayment",
    attribute: keyof RefundOriginalPayGatePaymentResponse,
    size?: number
  ): string;
  translateModelAttribute(
    model: // tslint:disable-next-line:max-union-size
    | "licensePlate"
      | "payGateRefund"
      | "payGateRefundPayment"
      | "payGateToTransferRefund"
      | "senderBankAccount"
      | "refund"
      | "transferRefundPayment"
      | "transferOriginalPayment"
      | "refundOriginalPayGatePayment",
    attribute: // tslint:disable-next-line:max-union-size
    | keyof LicensePlate
      | keyof PayGateRefundResponse
      | keyof PayGatePaymentResponse
      | keyof PayGateToTransferRefundDetail
      | keyof SenderBankAccount
      | keyof IRefund
      | keyof RefundTransferPayment
      | keyof TransferOriginalPayment
      | keyof RefundOriginalPayGatePaymentResponse
  ) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  @computed
  get self(): SelectableListViewModel<RefundApprovalsListViewModel<TEntity, TDetail, TFilter>, IRefund> {
    return this as any;
  }

  @bound
  canApprove(item: TEntity) {
    return item.permittedActions.approval.isAllowed && super.canApprove(item);
  }

  @bound
  translateModuleGeneral(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.${code}`, size);
  }
}
